import type { Room } from '@matchroom/shared';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, Badge } from '@/components/ui';

interface RoomCardProps {
  room: Room;
  index?: number;
}

const statusVariant = {
  LIVE: 'live' as const,
  WAITING: 'warning' as const,
  ENDED: 'default' as const,
};

export function RoomCard({ room, index = 0 }: RoomCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
    >
      <Link to={`/room/${room.id}`}>
        <Card className="h-full hover:border-brand-500/40 transition-all duration-200 group">
          <div className="flex items-start justify-between gap-2">
            <Badge variant={statusVariant[room.roomStatus]}>{room.roomStatus}</Badge>
            <span className="text-xs font-mono text-brand-400 bg-brand-500/10 px-2 py-0.5 rounded">
              {room.roomCode}
            </span>
          </div>

          <h3 className="mt-3 font-semibold text-lg group-hover:text-brand-300 transition-colors line-clamp-1">
            {room.name}
          </h3>

          {room.description ? (
            <p className="mt-1 text-sm text-surface-500 line-clamp-2">{room.description}</p>
          ) : null}

          {room.matchTitle ? (
            <p className="mt-3 text-sm text-accent-400 flex items-center gap-1.5">
              <span>⚽</span>
              <span className="truncate">{room.matchTitle}</span>
              {room.matchProvider ? (
                <span className="text-surface-500">· {room.matchProvider}</span>
              ) : null}
            </p>
          ) : null}

          <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-surface-500">
              <span>{room.currentParticipants}/{room.maxParticipants}</span>
              <span>·</span>
              <span className="capitalize">{room.visibility.toLowerCase()}</span>
              {room.hasPassword ? <span>🔒</span> : null}
            </div>
            <span className="text-xs text-brand-400 opacity-0 group-hover:opacity-100 transition-opacity">
              Join →
            </span>
          </div>
        </Card>
      </Link>
    </motion.div>
  );
}
